import { Link } from 'react-router-dom';
import { FolderOpen, Clock, Zap, ChevronRight, Star, Trash2, Activity, Cpu } from 'lucide-react';
import { useState, useEffect } from 'react';
import { api } from '../lib/api';

const defaultProjects = [
  {
    id: 1,
    title: 'Arduino LED Matrix Display',
    description: 'Build an 8x8 LED matrix display controlled by an Arduino Uno with MAX7219 driver chip. Display scrolling text and animations.',
    difficulty: 'Beginner',
    time: '2 hours',
    tags: ['Arduino', 'LED', 'Matrix', 'MAX7219'],
    color: 'green',
    featured: true,
  },
  {
    id: 2,
    title: 'ESP32 Weather Station',
    description: 'Full WiFi-connected weather station using ESP32, DHT22 temperature/humidity sensor, and BMP280 pressure sensor. Logs to cloud.',
    difficulty: 'Intermediate',
    time: '4 hours',
    tags: ['ESP32', 'WiFi', 'DHT22', 'BMP280'],
    color: 'cyan',
    featured: true,
  },
  {
    id: 3,
    title: 'Raspberry Pi Security Camera',
    description: 'Motion-detecting security camera using Raspberry Pi Zero W and Pi Camera Module. Sends alerts via Telegram.',
    difficulty: 'Intermediate',
    time: '3 hours',
    tags: ['Raspberry Pi', 'Camera', 'Python', 'Telegram'],
    color: 'purple',
    featured: false,
  },
  {
    id: 4,
    title: 'DIY LiPo Battery Charger',
    description: 'Design and build a safe single-cell LiPo charger circuit using TP4056 module with protection. Learn CC/CV charging.',
    difficulty: 'Beginner',
    time: '1 hour',
    tags: ['LiPo', 'TP4056', 'Battery', 'Power'],
    color: 'amber',
    featured: false,
  },
  {
    id: 5,
    title: 'Arduino CNC Mini Plotter',
    description: 'Build a small 2-axis CNC plotter using two stepper motors, Arduino, and GRBL firmware. Draw SVG designs on paper.',
    difficulty: 'Advanced',
    time: '8 hours',
    tags: ['Arduino', 'GRBL', 'Stepper', 'CNC'],
    color: 'green',
    featured: false,
  },
  {
    id: 6,
    title: 'ESP8266 Smart Home Switch',
    description: 'Turn any wall switch into a WiFi-controlled smart switch using ESP8266. Integrates with Home Assistant via MQTT.',
    difficulty: 'Intermediate',
    time: '3 hours',
    tags: ['ESP8266', 'MQTT', 'Home Assistant', 'IoT'],
    color: 'cyan',
    featured: false,
  },
  {
    id: 7,
    title: 'Raspberry Pi Retro Gaming Console',
    description: 'Build a fully functional retro gaming console with RetroPie on Raspberry Pi 4.',
    difficulty: 'Beginner',
    time: '2 hours',
    tags: ['Raspberry Pi', 'RetroPie', 'Gaming', 'Emulation'],
    color: 'purple',
    featured: false,
  },
  {
    id: 8,
    title: 'Arduino Oscilloscope',
    description: 'Turn your Arduino into a basic oscilloscope using the ADC input. View waveforms in Processing.',
    difficulty: 'Intermediate',
    time: '2 hours',
    tags: ['Arduino', 'ADC', 'Oscilloscope', 'Processing'],
    color: 'amber',
    featured: false,
  },
];

const difficultyColor: Record<string, string> = {
  Beginner: 'var(--color-primary)',
  Intermediate: 'var(--color-accent)',
  Advanced: 'var(--color-danger)',
};

const colorMap: Record<string, { text: string; bg: string; border: string }> = {
  green: { text: 'var(--color-primary)', bg: 'rgba(0,255,136,0.08)', border: 'rgba(0,255,136,0.2)' },
  cyan: { text: 'var(--color-secondary)', bg: 'rgba(0,207,255,0.08)', border: 'rgba(0,207,255,0.2)' },
  amber: { text: 'var(--color-accent)', bg: 'rgba(255,170,0,0.08)', border: 'rgba(255,170,0,0.2)' },
  purple: { text: 'var(--color-purple)', bg: 'rgba(168,85,247,0.08)', border: 'rgba(168,85,247,0.2)' },
};

export default function Projects() {
  const [userProjects, setUserProjects] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadUserProjects();
  }, []);

  const loadUserProjects = async () => {
    setLoading(true);
    try {
      const data = await api.getProjects();
      setUserProjects(data);
    } catch (err) {
      console.error("Failed to load vault projects:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (e: any, id: string) => {
    e.preventDefault();
    if (window.confirm("Delete this session from your vault?")) {
      await api.deleteProject(id);
      loadUserProjects();
    }
  };

  return (
    <div style={{ minHeight: 'calc(100vh - 64px)', padding: '0 1.5rem 3rem' }}>
      <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
        <div style={{ padding: '2.5rem 0 2rem', textAlign: 'center' }}>
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '0.75rem' }}>
            <div style={{ width: '52px', height: '52px', background: 'rgba(168,85,247,0.1)', border: '1px solid rgba(168,85,247,0.3)', borderRadius: '14px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <FolderOpen size={24} color="var(--color-purple)" />
            </div>
          </div>
          <h1 style={{ fontSize: '2rem', fontWeight: 800, marginBottom: '0.5rem' }}>
            DRTKNG <span style={{ color: 'var(--color-purple)' }}>Vault</span> & Projects
          </h1>
          <p style={{ color: 'var(--text-muted)', maxWidth: '480px', margin: '0 auto', fontSize: '0.9rem' }}>
            Access your synchronized diagnostics, drone benchmarks, and PCB builds across all devices.
          </p>
        </div>

        {/* User Vault Section */}
        <div style={{ marginBottom: '3rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <p style={{ fontSize: '0.78rem', color: 'var(--color-purple)', fontFamily: 'var(--font-mono)', letterSpacing: '0.05em', fontWeight: 800 }}>⚡ YOUR SYNCHRONIZED VAULT:</p>
            {loading && <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>Syncing...</span>}
          </div>

          {!loading && userProjects.length === 0 ? (
            <div style={{ padding: '3rem', border: '1px dashed var(--border-color)', borderRadius: '16px', textAlign: 'center', background: 'rgba(255,255,255,0.01)' }}>
               <FolderOpen size={32} color="var(--text-muted)" style={{ marginBottom: '1rem', opacity: 0.3 }} />
               <p style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>No saved sessions found. Save a diagnostic or studio project to see it here.</p>
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1rem' }}>
              {userProjects.map((p: any) => (
                <Link 
                  key={p.id} 
                  to={p.type === 'chat' ? '/diagnose' : '/studio'} 
                  state={{ projectId: p.id }}
                  style={{ 
                    background: 'var(--bg-surface)', border: '1px solid var(--border-color)', borderRadius: '14px', 
                    padding: '1.25rem', textDecoration: 'none', transition: 'all 0.2s', position: 'relative',
                    overflow: 'hidden'
                  }}
                  onMouseEnter={(e: any) => { (e.currentTarget as HTMLElement).style.borderColor = 'rgba(168,85,247,0.3)'; (e.currentTarget as HTMLElement).style.background = 'rgba(168,85,247,0.03)'; }}
                  onMouseLeave={(e: any) => { (e.currentTarget as HTMLElement).style.borderColor = 'var(--border-color)'; (e.currentTarget as HTMLElement).style.background = 'var(--bg-surface)'; }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
                    <div style={{ display: 'flex', gap: '0.4rem' }}>
                      {p.type === 'chat' ? <Cpu size={14} color="var(--color-primary)" /> : <Activity size={14} color="var(--color-secondary)" />}
                      <span style={{ fontSize: '0.65rem', fontWeight: 800, color: 'var(--text-muted)', textTransform: 'uppercase' }}>{p.type}</span>
                    </div>
                    <button onClick={(e) => handleDelete(e, p.id)} style={{ background: 'transparent', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', padding: '0.2rem' }} onMouseEnter={e => e.currentTarget.style.color = '#ff3366'} onMouseLeave={e => e.currentTarget.style.color = 'var(--text-muted)'}>
                      <Trash2 size={12} />
                    </button>
                  </div>
                  <h3 style={{ fontSize: '0.9rem', fontWeight: 700, marginBottom: '0.4rem', color: 'var(--text-primary)' }}>{p.name}</h3>
                  <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                    <Clock size={10} /> {new Date(p.updatedAt).toLocaleDateString()} at {new Date(p.updatedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </div>
                  {/* Decorative Handshake Accent */}
                  <div style={{ position: 'absolute', bottom: '-5px', right: '-5px', opacity: 0.05 }}><Zap size={40} color="var(--color-purple)" /></div>
                </Link>
              ))}
            </div>
          )}
        </div>

        <div style={{ marginBottom: '2rem' }}>
          <p style={{ fontSize: '0.78rem', color: 'var(--text-muted)', fontFamily: 'var(--font-mono)', letterSpacing: '0.05em', marginBottom: '0.75rem' }}>★ FEATURED GUIDES:</p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '1rem' }}>
            {defaultProjects.filter(p => p.featured).map(project => {
              const c = colorMap[project.color];
              return (
                <Link key={project.id} to="/diagnose" style={{ background: c.bg, border: `1px solid ${c.border}`, borderRadius: '16px', padding: '1.75rem', transition: 'all 0.25s ease', textDecoration: 'none' }}
                  onMouseEnter={(e: any) => { (e.currentTarget as HTMLElement).style.transform = 'translateY(-3px)'; }}
                  onMouseLeave={(e: any) => { (e.currentTarget as HTMLElement).style.transform = 'translateY(0)'; }}>
                  <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.75rem', flexWrap: 'wrap' }}>
                    <span style={{ fontSize: '0.65rem', fontWeight: 700, padding: '0.12rem 0.4rem', borderRadius: '4px', background: `${difficultyColor[project.difficulty]}15`, color: difficultyColor[project.difficulty], border: `1px solid ${difficultyColor[project.difficulty]}33`, fontFamily: 'var(--font-mono)' }}>{project.difficulty}</span>
                    <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: '0.2rem' }}><Clock size={11} /> {project.time}</span>
                    <span style={{ fontSize: '0.65rem', color: 'var(--color-accent)', display: 'flex', alignItems: 'center', gap: '0.2rem', fontWeight: 700 }}><Star size={11} fill="currentColor" /> FEATURED</span>
                  </div>
                  <h3 style={{ fontSize: '1.05rem', fontWeight: 700, marginBottom: '0.5rem', color: 'var(--text-primary)' }}>{project.title}</h3>
                  <p style={{ fontSize: '0.84rem', color: 'var(--text-secondary)', lineHeight: 1.6, marginBottom: '1rem' }}>{project.description}</p>
                  <div style={{ display: 'flex', gap: '0.3rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
                    {project.tags.map(tag => <span key={tag} style={{ fontSize: '0.65rem', padding: '0.12rem 0.35rem', borderRadius: '3px', background: 'var(--bg-surface-2)', color: 'var(--text-muted)', border: '1px solid var(--border-color)', fontFamily: 'var(--font-mono)' }}>{tag}</span>)}
                  </div>
                  <div style={{ display: 'inline-flex', alignItems: 'center', gap: '0.3rem', fontSize: '0.82rem', fontWeight: 600, color: c.text, textDecoration: 'none' }}>
                    AI Build Guide <ChevronRight size={13} />
                  </div>
                </Link>
              );
            })}
          </div>
        </div>

        <div>
          <p style={{ fontSize: '0.78rem', color: 'var(--text-muted)', fontFamily: 'var(--font-mono)', letterSpacing: '0.05em', marginBottom: '0.75rem' }}>ALL PROJECT GUIDES:</p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            {defaultProjects.filter(p => !p.featured).map(project => {
              const c = colorMap[project.color];
              return (
                <div key={project.id} style={{ background: 'var(--bg-surface)', border: '1px solid var(--border-color)', borderRadius: '12px', padding: '1.25rem', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '1rem', flexWrap: 'wrap', transition: 'border-color 0.2s ease' }}
                  onMouseEnter={(e: any) => { (e.currentTarget as HTMLElement).style.borderColor = c.border; }}
                  onMouseLeave={(e: any) => { (e.currentTarget as HTMLElement).style.borderColor = 'var(--border-color)'; }}>
                  <div style={{ flex: 1, minWidth: '200px' }}>
                    <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.4rem', alignItems: 'center', flexWrap: 'wrap' }}>
                      <span style={{ fontSize: '0.65rem', fontWeight: 700, padding: '0.12rem 0.4rem', borderRadius: '4px', background: `${difficultyColor[project.difficulty]}15`, color: difficultyColor[project.difficulty], border: `1px solid ${difficultyColor[project.difficulty]}33`, fontFamily: 'var(--font-mono)' }}>{project.difficulty}</span>
                      <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: '0.2rem' }}><Clock size={11} /> {project.time}</span>
                    </div>
                    <h3 style={{ fontSize: '0.92rem', fontWeight: 700, marginBottom: '0.3rem', color: 'var(--text-primary)' }}>{project.title}</h3>
                    <p style={{ fontSize: '0.82rem', color: 'var(--text-secondary)', lineHeight: 1.5 }}>{project.description}</p>
                    <div style={{ display: 'flex', gap: '0.3rem', flexWrap: 'wrap', marginTop: '0.4rem' }}>
                      {project.tags.map(tag => <span key={tag} style={{ fontSize: '0.62rem', padding: '0.1rem 0.3rem', borderRadius: '3px', background: 'var(--bg-surface-2)', color: 'var(--text-muted)', border: '1px solid var(--border-color)', fontFamily: 'var(--font-mono)' }}>{tag}</span>)}
                    </div>
                  </div>
                  <div style={{ display: 'inline-flex', alignItems: 'center', gap: '0.3rem', flexShrink: 0, fontSize: '0.78rem', fontWeight: 600, color: c.text, textDecoration: 'none', padding: '0.4rem 0.7rem', border: `1px solid ${c.border}`, borderRadius: '8px', background: c.bg }}>
                    Guide <ChevronRight size={12} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div style={{ marginTop: '2.5rem', background: 'rgba(168,85,247,0.06)', border: '1px solid rgba(168,85,247,0.2)', borderRadius: '14px', padding: '2rem', textAlign: 'center' }}>
          <Zap size={22} color="var(--color-purple)" style={{ marginBottom: '0.75rem' }} />
          <h3 style={{ fontSize: '1rem', fontWeight: 700, marginBottom: '0.4rem' }}>Have a project idea?</h3>
          <p style={{ color: 'var(--text-muted)', fontSize: '0.83rem', marginBottom: '1rem' }}>Tell ElectrAI what you want to build and get a step-by-step component list and circuit guide.</p>
          <Link to="/diagnose" style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', background: 'rgba(168,85,247,0.15)', color: 'var(--color-purple)', border: '1px solid rgba(168,85,247,0.3)', padding: '0.5rem 1rem', borderRadius: '8px', fontSize: '0.83rem', fontWeight: 600, textDecoration: 'none' }}>
            Ask ElectrAI <ChevronRight size={13} />
          </Link>
        </div>
      </div>
    </div>
  );
}
